const dljs = require("damerau-levenshtein-js");

export interface ReviewAnalysisResult {
    isCorrect: boolean
    hasTypo?: boolean
    isDifferentWord?: boolean
}

export type SensitivityConfig = "None" | {ratio: number}

async function analyzeReview(userAnswer: string, acceptedAnswers: string | string[], notAcceptedAnswers: string[], sensitivity: SensitivityConfig) : Promise<ReviewAnalysisResult> {

    const evaluate = ({distance, targetAnswer} : {distance: number, targetAnswer: string}) => {
        const allowedDistance = sensitivity !== "None" ? Math.floor(targetAnswer.length / sensitivity.ratio) : 0;
        return distance <= allowedDistance;
    }

    const normalizeWord = (w : string) => w.toLowerCase()
        .replace(" ", "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\p{L}\d\s]/gu, "");

    const digitToStr = new Map<string, string>([
        ["0", "zero"], ["1", "one"], ["2", "two"], ["3", "three"], ["4", "four"],
        ["5", "five"], ["6", "six"], ["7", "seven"], ["8", "eight"], ["9", "nine"], ["10", "ten"]
    ]);

    const strToDigit = new Map<string, string>([
        ["zero", "0"], ["one", "1"], ["two", "2"], ["three", "3"], ["four", "4"],
        ["five", "5"], ["six", "6"], ["seven", "7"], ["eight", "8"], ["nine", "9"], ["ten", "10"]
    ]);

    const expanders = [
        (words: string[]) => words.filter(w => w.includes('(') && w.includes(')')).map(w => w.replace(/ *\([^)]*\) */g, "")),
        (words: string[]) => words.filter(w => w.startsWith("the ") || w.startsWith("The ")).map(w => w.substring(4)),
        (words: string[]) => words.filter(w => w.startsWith("to ") || w.startsWith("To ")).map(w => w.substring(3)),
        (words: string[]) => words.map(w => w.split(" ").map(part => digitToStr.has(part) ? digitToStr.get(part) : strToDigit.has(part) ? strToDigit.get(part) : part).join(" ")).filter((w, idx) => w!== words[idx])
    ];

    const expanderActivator = (acceptedAnswers: string[]) => expanders.reduce((allAcceptedAnswers, expander) =>
        allAcceptedAnswers.concat(expander(allAcceptedAnswers)), acceptedAnswers).map(normalizeWord);

    const allAcceptedAnswers = expanderActivator(Array.isArray(acceptedAnswers) ? acceptedAnswers : [acceptedAnswers]);

    userAnswer = normalizeWord(userAnswer);

    const promises = allAcceptedAnswers.map(acceptedAnswer =>
        dljs.distanceProm(userAnswer, acceptedAnswer).then((distance: number) => ({distance, targetAnswer: acceptedAnswer})));

    const results = await Promise.all(promises);

    let foundCorrectWithTypo = false;
    for (const {distance, targetAnswer} of results) {
        if (distance === 0) {
            return {isCorrect: true, hasTypo: false};
        } else if (evaluate({distance, targetAnswer})) {
            foundCorrectWithTypo = true;
        }
    }

    if (foundCorrectWithTypo) {
        return {isCorrect: true, hasTypo: true};
    }

    const allNotAcceptedAnswers = expanderActivator(notAcceptedAnswers);
    const notAcceptedPromises = allNotAcceptedAnswers.map(notAcceptedAnswer =>
        dljs.distanceProm(userAnswer, notAcceptedAnswer).then((distance: number) => ({distance, targetAnswer: notAcceptedAnswer})));
    const notAcceptedResults = await Promise.all(notAcceptedPromises);
    if (notAcceptedResults.filter(evaluate).length > 0) {
        return {isCorrect: false, isDifferentWord: true};
    }

    return {isCorrect: false};
}

export default analyzeReview;